import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FliptProvider, useFliptContext, useFliptSelector } from '@flipt-io/flipt-client-react';
import { FeatureFlagContext } from './FeatureFlagContext';
import { isWasmRuntimeError, supportsWasmReferenceTypes } from './wasmSupport';

/**
 * 스냅샷 갱신 주기(초). Flipt 서버를 이 간격으로 폴링한다.
 *
 * 플래그는 실시간 반영이 필요한 종류가 아니다 — 켜고 끈 값이 5분 안에 퍼지면
 * 충분하고, 그 대가로 CloudFront 뒤 Flipt 호출이 절반으로 준다. _app 에 provider 가
 * 올라가 전 페이지에서 폴링하게 되면서 이 비용이 실제로 커졌다.
 *
 * 폴링 실패는 client.refresh() 가 error 를 세팅해 평가를 fallback 으로 되돌리는
 * 경로이기도 하다(FeatureFlagBridge 의 onError 주석). 주기를 늘리면 그 표면도 같이
 * 줄어든다.
 */
const DEFAULT_UPDATE_INTERVAL = 300;

/**
 * Maximum Flipt initialization attempts on supported browsers.
 *
 * The SDK counts the initial attempt, then waits 1s, 2s, 4s, and 8s before
 * attempts 2–5, so a permanently failing client gives up at about 15 seconds
 * instead of retrying every 30 seconds for the lifetime of the tab.
 *
 * 15초는 FeatureFlagBridge 가 화면을 기본값으로 넘기는 10초(STORE_SYNC_MAX_ATTEMPTS)
 * 바로 뒤다 — 늦게 성공하면 useFliptBoolean 구독으로 실제 값이 반영되므로, 이 간격이
 * 좁을수록 "기본값 → 실제값" 으로 화면이 바뀌는 창이 짧다.
 */
const DEFAULT_MAX_RETRY_ATTEMPTS = 5;

/**
 * SDK 의 `maxRetryAttempts` 는 이름과 달리 **총 시도 횟수** 다 — 첫 시도도 센다.
 * 그래서 0 은 "재시도 없이 1번" 이 아니라 init 루프가 시작조차 하지 않는 값이다
 * (`attempt < 0` 이 거짓). fetch 도 WASM 청크 로드도 일어나지 않는다.
 *
 * "초기화 하지 마" 를 표현하는 SDK 의 유일한 스위치라 개념을 이 이름으로 복원한다.
 * FliptProvider 는 `options`·`children` 만 받아 `enabled` 같은 대안이 없다.
 * 이 셈법은 SDK 계약이므로 `FliptProvider.skipContract.test.tsx` 가 실제 SDK 로 고정한다.
 */
const SKIP_INITIALIZATION_ATTEMPTS = 0;

/**
 * Why Flipt initialization was skipped. `null` means it runs normally.
 *
 * Skipping is a single mechanism — `maxRetryAttempts: 0` — so the SDK never
 * calls `FliptClient.init`: no snapshot fetch, no 400KB WASM chunk, no engine.
 * Every consumer then reads its fallback (SSR seed or default) through
 * `useFliptBoolean`, exactly as it would after a terminal init failure.
 *
 * - `server`: SSR render. Effects never run there, so this is only explicit.
 * - `disabled`: host opted out (e.g. play inside the quiz iframe).
 * - `wasm-unsupported`: `WebAssembly.validate` rejects `externref` up front.
 * - `wasm-runtime-error`: the engine compiled but blew up at runtime — WebKit
 *   16.3 and below has a known externref-table bug (`Out of bounds table
 *   access` in `engine_snapshot`) that only surfaces once it actually runs, and
 *   only on some devices of the same OS version, so it cannot be probed. Mostly
 *   iOS 15/16, but macOS Safari 16.3 shows up too — it is a WebKit bug, not an
 *   iOS one.
 */
export type InitializationSkipReason = 'server' | 'disabled' | 'wasm-unsupported' | 'wasm-runtime-error';

/**
 * Polling interval (ms) for the isLoading sync workaround.
 *
 * `useFliptContext()` is a plain `useContext` — it hands back the mutable
 * `storeRef.current` without subscribing. Only `useFliptSelector` subscribes.
 * So when the SDK flips `isLoading` and calls `notify()`, this component is
 * never re-rendered and `isLoading` stays stuck at true. We poll the raw store
 * to detect the mismatch and force a re-render.
 */
const STORE_SYNC_POLL_MS = 500;

/** Maximum polling attempts before giving up (500ms × 20 = 10s). */
const STORE_SYNC_MAX_ATTEMPTS = 20;

/**
 * Selects the *terminal* `error` out of the Flipt store.
 *
 * The SDK assigns `error` and notifies on every failed init attempt, including
 * the ones it is about to retry (`isLoading` stays true while retrying). Only
 * when it gives up does it also flip `isLoading` to false. Reporting while
 * `isLoading` is true would count every transient blip that the retry then
 * recovers from — Sentry could not tell a recovered session from a stuck one.
 * Dropping the error until `isLoading` is false leaves exactly two reportable
 * cases: init exhausted all retries, or a later `refresh()` poll failed.
 * WASM engine failures are excluded too — those take the `selectWasmRuntimeError`
 * route and end as a skip, not an error.
 *
 * Declared at module scope on purpose — an inline selector would be a new
 * function every render, replacing `useSyncExternalStore`'s getSnapshot each
 * time. Returning only the error also means a re-render happens *only* when
 * the error changes, not on every store notification.
 */
const selectFliptError = (_client: unknown, isLoading: boolean, error: Error | null) => isLoading || isWasmRuntimeError(error) ? null : error;

/**
 * Selects a WASM engine failure the moment the SDK records it — while it is
 * still `isLoading` and about to retry. Catching it on attempt 1 is what lets
 * the provider flip `maxRetryAttempts` to 0 before attempt 2 runs.
 */
const selectWasmRuntimeError = (_client: unknown, _isLoading: boolean, error: Error | null) => isWasmRuntimeError(error) ? error : null;
export interface FeatureFlagProviderProps {
  /** Feature flag server URL */
  serverUrl: string;
  /** Flipt namespace for flag scoping */
  namespace: string;
  /** Environment (e.g., 'development', 'live') */
  environment: string;
  /** User-level ID for flag evaluation — user hashId (play) or quizUserId (quiz).
   *  Empty string when not logged in. Combined with deviceId as fallback internally. */
  userId: string;
  /** Device-level ID — stable across login/logout, used as WASM instance key and device bucketing */
  deviceId: string;
  /** Polling interval in seconds (default: 300) */
  updateInterval?: number;
  /** Pre-evaluated boolean flag values from SSR (seed) */
  ssrFlags?: Record<string, boolean>;
  /** Pre-evaluated variant flag values from SSR (seed) */
  ssrVariants?: Record<string, string>;
  /** Reports Flipt failures to the host app's error tracker.
   *  The package never picks a logger — the app injects one. Deduped by error
   *  message, so a repeatedly failing poll reports once rather than every
   *  `updateInterval`.
   *
   *  Fires for: init failure after the SDK has exhausted its retries (chunk
   *  load, server down, 4xx/5xx, network), and `client.refresh()` failure during
   *  polling. Not reported: failures the SDK is still retrying, and WASM engine
   *  failures (those become `onInitializationSkipped('wasm-runtime-error')`).
   *  In every case evaluation falls back to its default value, so this is
   *  observability only — nothing about the user-facing behavior changes. */
  onError?: (error: unknown) => void;
  /** Called once per mount when Flipt initialization is skipped for a reason
   *  the host did not ask for: `'wasm-unsupported'` or `'wasm-runtime-error'`.
   *  Observability only — evaluation already falls back to seed/default values.
   *  Not routed through `onError` because neither is a failure to page on: the
   *  host app should log at debug level so the affected browser mix can be
   *  measured. `'disabled'` is the host's own decision and `'server'` never
   *  runs effects, so neither is reported. */
  onInitializationSkipped?: (reason: InitializationSkipReason) => void;
  /** When true, Flipt is never initialized — no fetch, no WASM — and every
   *  evaluation returns its fallback. Use where flags are unnecessary
   *  (e.g. play embedded in the quiz iframe). */
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * React provider that wraps @flipt-io/flipt-client-react's FliptProvider
 * while preserving our custom interface (ssrFlags, ssrVariants, userId, deviceId, isReady).
 *
 * Props are flat primitives to avoid reference instability from config objects.
 * An inner bridge component reads Flipt's isLoading state once and provides it
 * via our context, preventing N-subscription overhead from multiple hooks.
 *
 * Targeting context (e.g., spaceHashId) is NOT managed here — each domain hook
 * passes its own context via the per-call `context` option in useFeatureFlag.
 */
export function FeatureFlagProvider({
  serverUrl,
  namespace,
  environment,
  userId,
  deviceId,
  updateInterval = DEFAULT_UPDATE_INTERVAL,
  ssrFlags,
  ssrVariants,
  onError,
  onInitializationSkipped,
  disabled = false,
  children
}: FeatureFlagProviderProps) {
  const userEntityId = userId || deviceId;
  const deviceEntityId = deviceId;
  // 예전엔 deviceId 가 없으면 FliptProvider 를 아예 건너뛰고 children 만 렌더했는데,
  // 그러면 소비 코드의 useFeatureFlag 가 "must be used within a FliptProvider" 로
  // **throw** 한다 — 서버 렌더에서는 getDeviceId() 가 쿠키 없이 항상 비므로
  // SSG/SSR 페이지가 통째로 깨진다. context 는 늘 제공하고, 나가는 요청만 막는다.
  //
  // 조건은 deviceId 유무가 아니라 **서버인지**다. 스냅샷 fetch 는 네임스페이스 단위라
  // entityId 와 무관하고(평가만 WASM 이 entityId 로 한다), 그래서 entityId 가 없다고
  // 초기화를 막을 이유가 없다. 서버에서는 어차피 effect 가 돌지 않아 WASM 도 안 뜬다.
  const isServer = typeof window === 'undefined';

  // 마운트 뒤에 일어나는 사건만 상태다. 나머지 skip 원인(서버·disabled·프로브)은
  // 렌더마다 계산되는 파생값 — disabled 를 상태 초기값에 넣으면 이후 토글을 놓친다.
  // Bridge 는 SDK store 에서 WASM 엔진 실패를 보면 이 setter 로 보고만 하고,
  // 그걸 "초기화 생략" 으로 해석하는 건 여기 skipReason 이다.
  const [wasmRuntimeFailed, setWasmRuntimeFailed] = useState(false);
  const markWasmRuntimeFailed = useCallback(() => setWasmRuntimeFailed(true), []);
  const skipReason: InitializationSkipReason | null = isServer ? 'server' : disabled ? 'disabled' : !supportsWasmReferenceTypes() ? 'wasm-unsupported' : wasmRuntimeFailed ? 'wasm-runtime-error' : null;
  const shouldSkipInitialization = skipReason !== null;

  // 마운트당 한 번만 알린다. 호스트 앱은 보통 인라인 화살표를 넘기므로 콜백 참조가
  // 렌더마다 바뀐다 — 그걸 effect 의존성으로 두면 리렌더마다 debug 로그가 쌓인다.
  // skip 이유는 세션 안에서 바뀌어도 첫 결정이 관측 대상이라 첫 발화 뒤에는 무시한다.
  // disabled 는 앱이 스스로 내린 결정이라 알릴 게 없다 — 관측 대상은 앱이 요청하지
  // 않았는데 건너뛴 경우(브라우저 비호환)만이다.
  const hasReportedSkipRef = useRef(false);
  useEffect(() => {
    if (skipReason === null || skipReason === 'disabled') return;
    if (hasReportedSkipRef.current) return;
    hasReportedSkipRef.current = true;
    onInitializationSkipped?.(skipReason);
  }, [skipReason, onInitializationSkipped]);

  // skip 은 메커니즘 하나 — SKIP_INITIALIZATION_ATTEMPTS. 진행 중 실패
  // (wasm-runtime-error) 로 나중에 바뀌면 SDK effect 가 재실행되며 돌던 루프는
  // isMounted=false 로 종료된다 — 남은 재시도가 실행되지 않는다.
  const fliptOptions = useMemo(() => ({
    namespace,
    url: serverUrl,
    environment,
    updateInterval,
    maxRetryAttempts: shouldSkipInitialization ? SKIP_INITIALIZATION_ATTEMPTS : DEFAULT_MAX_RETRY_ATTEMPTS
  }), [namespace, serverUrl, environment, updateInterval, shouldSkipInitialization]);

  // key 는 **호스트가 내린 결정**만 담는다. remount 는 FliptProvider 아래 서브트리
  // 전체(play 의 <Play>, quiz 의 페이지)를 버리는 동작이라, 마운트 뒤에 일어나는
  // wasmRuntimeFailed 를 섞으면 iOS 15/16 에서 WASM 이 터지는 순간 실행 중인 게임이
  // 통째로 초기화된다 — 이 PR 이 줄이려는 피해보다 큰 피해다.
  //
  // 재시도를 끊는 일은 key 가 아니라 options 변경이 한다(위 fliptOptions 주석). 그래서
  // key 에서 런타임 skip 을 빼도 "RuntimeError → 1회차 후 0회" 계약은 그대로다.
  return <FliptProvider key={`${deviceEntityId}-${disabled}`} options={fliptOptions} data-sentry-element="FliptProvider" data-sentry-component="FeatureFlagProvider" data-sentry-source-file="FeatureFlagProvider.tsx">
      <FeatureFlagBridge userEntityId={userEntityId} deviceEntityId={deviceEntityId} shouldSkipInitialization={shouldSkipInitialization} markWasmRuntimeFailed={markWasmRuntimeFailed} ssrFlags={ssrFlags} ssrVariants={ssrVariants} onError={onError} data-sentry-element="FeatureFlagBridge" data-sentry-source-file="FeatureFlagProvider.tsx">
        {children}
      </FeatureFlagBridge>
    </FliptProvider>;
}

/**
 * Inner bridge that reads useFliptContext() exactly once,
 * then exposes isReady via our FeatureFlagContext.
 * This prevents N hooks from each subscribing to Flipt independently.
 *
 * Includes a workaround for how @flipt-io/flipt-client-react exposes its store:
 * `useFliptContext()` is a plain `useContext` with no subscription, so the
 * store's `isLoading` transition never re-renders this component. We poll the
 * raw store object to detect the mismatch and force a re-render. Errors take
 * the other route — `useFliptSelector`, which does subscribe.
 */
function FeatureFlagBridge({
  userEntityId,
  deviceEntityId,
  shouldSkipInitialization,
  markWasmRuntimeFailed,
  ssrFlags,
  ssrVariants,
  onError,
  children
}: {
  userEntityId: string;
  deviceEntityId: string;
  shouldSkipInitialization: boolean;
  /** WASM 엔진 실패를 Provider 에 보고한다. 감시자는 사실만 올리고, 그것을
   *  "초기화 생략" 으로 해석해 재시도를 끊는 결정은 Provider 의 skipReason 이 한다. */
  markWasmRuntimeFailed: () => void;
  ssrFlags?: Record<string, boolean>;
  ssrVariants?: Record<string, string>;
  onError?: (error: unknown) => void;
  children: React.ReactNode;
}) {
  const fliptCtx = useFliptContext();
  const {
    isLoading
  } = fliptCtx;

  // Workaround: poll the raw store to detect stale useSyncExternalStore state.
  // The fliptCtx object is a mutable ref (storeRef.current from FliptProvider),
  // so reading its properties in setInterval gives the live value even when
  // useSyncExternalStore fails to notify React.
  const [syncFix, setSyncFix] = useState(0);
  const [hasGivenUp, setHasGivenUp] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const pollCountRef = useRef(0);
  useEffect(() => setHasMounted(true), []);
  useEffect(() => {
    if (shouldSkipInitialization) return;
    if (!isLoading) return; // already synced, no need to poll
    if (syncFix > 0) return; // workaround already fired, stop polling
    if (hasGivenUp) return; // already gave up, stop polling

    pollCountRef.current = 0;
    const id = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= STORE_SYNC_MAX_ATTEMPTS) {
        clearInterval(id);
        console.warn(`[@zep/feature-flag] useSyncExternalStore shim polling gave up after ${STORE_SYNC_MAX_ATTEMPTS} attempts. ` + 'Flipt WASM may have failed to initialize. SSR seed values will continue to be used.');
        setHasGivenUp(true);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = fliptCtx as any;
      if (raw.client && raw.isLoading === false) {
        setSyncFix(n => n + 1); // force re-render to pick up new state
      }
    }, STORE_SYNC_POLL_MS);
    return () => clearInterval(id);
  }, [isLoading, fliptCtx, syncFix, hasGivenUp, shouldSkipInitialization]);

  // Treat as ready when: Flipt initialized successfully, sync workaround fired,
  // polling gave up, or the browser cannot compile Flipt's WASM. Keep the first
  // client render loading to match SSR, then expose SSR seed/default values.
  const effectiveIsLoading = shouldSkipInitialization && hasMounted ? false : hasGivenUp ? false : syncFix > 0 ? (fliptCtx as unknown as Record<string, unknown>).isLoading !== false : isLoading;

  // Flipt 실패를 호스트 앱의 에러 트래커로 흘린다. 실패해도 평가는 기본값으로
  // degrade 하므로 화면은 멀쩡하고, 그래서 계측이 없으면 장애가 조용히 지나간다 —
  // 실험 분석에서는 그 구간이 통째로 대조군처럼 보인다.
  //
  // 반드시 useFliptSelector 로 읽는다. useFliptContext() 는 구독이 없어서
  // (위 STORE_SYNC_POLL_MS 주석) SDK 가 error 를 세팅하고 notify() 해도 이
  // 컴포넌트가 리렌더되지 않는다 — 그러면 보고는 부모가 우연히 리렌더될 때만
  // 일어나고, 랜딩만 보고 이탈하는 방문자에겐 영영 보고되지 않는다.
  const fliptError = useFliptSelector(selectFliptError);

  // WASM 엔진 실패는 에러가 아니라 "이 브라우저는 안 된다" 는 사실이다. 재시도해도
  // 매번 같은 자리에서 터지므로(WebKit ≤16.3 externref 테이블 버그) 1회차에서 보자마자
  // Provider 로 올린다 — Provider 가 maxRetryAttempts 를 0 으로 바꿔 남은 재시도를
  // 끊고, onInitializationSkipped('wasm-runtime-error') 로 알린다.
  const wasmRuntimeError = useFliptSelector(selectWasmRuntimeError);
  useEffect(() => {
    if (!wasmRuntimeError) return;
    markWasmRuntimeFailed();
  }, [wasmRuntimeError, markWasmRuntimeFailed]);

  // 같은 원인의 실패는 한 번만 보낸다. 객체 신원으로 비교하면 안 된다 —
  // refresh() 실패는 폴링마다 rejection 을 그대로 대입해서 내용이 같아도 매번
  // 새 Error 객체다. 그대로 두면 탭을 열어둔 시간만큼 같은 에러가 쌓인다.
  const reportedErrorKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!fliptError) return;
    const key = fliptError.message || String(fliptError);
    if (key === reportedErrorKeyRef.current) return;
    reportedErrorKeyRef.current = key;
    onError?.(fliptError);
  }, [fliptError, onError]);

  // SSR seed values: capture once at mount — after Flipt initializes,
  // WASM evaluation replaces these via useFliptBoolean/useFliptVariant defaults.
  const ssrFlagsRef = useRef(ssrFlags);
  const ssrVariantsRef = useRef(ssrVariants);
  const contextValue = useMemo(() => ({
    userEntityId,
    deviceEntityId,
    isReady: !effectiveIsLoading,
    ssrFlags: ssrFlagsRef.current,
    ssrVariants: ssrVariantsRef.current
  }), [userEntityId, deviceEntityId, effectiveIsLoading]);
  return <FeatureFlagContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="FeatureFlagBridge" data-sentry-source-file="FeatureFlagProvider.tsx">
      {children}
    </FeatureFlagContext.Provider>;
}